import { ReactNode, useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { AppSidebar } from './AppSidebar';
import { EmpresaSeletor } from './EmpresaSeletor';
import { SidebarProvider, SidebarTrigger } from '@/components/ui/sidebar';
import { Loader2, Bell, CheckCircle, AlertCircle, Info, Target, User, LogOut, Building2 } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { notificacaoService } from '@/services/api';
import { Notificacao as NotificacaoType } from '@/types';
import { ThemeToggle } from '@/components/ThemeToggle';
import { PrimeirosPassosBotao, PrimeirosPassosMenuItem, PrimeirosPassosProvider } from '@/components/onboarding/PrimeirosPassos';

interface AppLayoutProps {
  children: ReactNode;
}

// Mapear tipos do backend para tipos do frontend
const mapNotificationType = (tipo: NotificacaoType['tipo']): 'success' | 'warning' | 'info' | 'okr' => {
  switch (tipo) {
    case 'aprovacao_conclusao':
    case 'novo_okr':
    case 'crm_negocio_ganho':
      return 'success';
    case 'rejeicao_conclusao':
    case 'prazo_proximo':
    case 'crm_negocio_perdido':
    case 'crm_atividade_vencida':
      return 'warning';
    case 'alteracao_key_result':
    case 'solicitacao_conclusao':
      return 'okr';
    default:
      return 'info';
  }
};

const getNotificationIcon = (tipo: NotificacaoType['tipo']) => {
  const type = mapNotificationType(tipo);
  switch (type) {
    case 'success':
      return <CheckCircle className="h-4 w-4 text-green-500" />;
    case 'warning':
      return <AlertCircle className="h-4 w-4 text-amber-500" />;
    case 'okr':
      return <Target className="h-4 w-4 text-primary" />;
    default:
      return <Info className="h-4 w-4 text-blue-500" />;
  }
};

const formatTimeAgo = (date: Date): string => {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 60) return `${diffMins}min atrás`;
  if (diffHours < 24) return `${diffHours}h atrás`;
  return `${diffDays}d atrás`;
};

const getNotificationTimestamp = (notification: NotificacaoType): Date | null => {
  const rawDate = notification.created_at || notification.createdAt;
  if (!rawDate) return null;

  const date = new Date(rawDate);
  if (Number.isNaN(date.getTime())) return null;

  return date;
};

const getNotificationRedirectPath = (notification: NotificacaoType): string => {
  if (
    notification.tipo === 'solicitacao_conclusao' ||
    notification.tipo === 'aprovacao_conclusao' ||
    notification.tipo === 'rejeicao_conclusao'
  ) {
    return '/aprovacoes';
  }

  if (
    (notification.tipo === 'crm_negocio_atribuido' ||
      notification.tipo === 'crm_negocio_ganho' ||
      notification.tipo === 'crm_negocio_perdido' ||
      notification.tipo === 'crm_atividade_vencida') &&
    notification.relacionado_id
  ) {
    return `/crm/negocios/${notification.relacionado_id}`;
  }

  if (notification.okr_id || notification.key_result_id) {
    return '/okrs';
  }

  return '/dashboard';
};

export function AppLayout({ children }: AppLayoutProps) {
  const {
    user,
    isLoading,
    isAuthenticated,
    logout,
    empresaAtiva,
    trocarEmpresa,
    empresasDisponiveis,
    empresaFiltroId,
    setEmpresaFiltroId,
  } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<NotificacaoType[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoadingNotifications, setIsLoadingNotifications] = useState(true);

  const loadNotifications = async () => {
    if (!user?.id) return;
    
    try {
      setIsLoadingNotifications(true);
      const response = await notificacaoService.getAll();
      if (response.success) {
        setNotifications(response.data);
      }

      const countResponse = await notificacaoService.contarNaoLidas();
      if (countResponse.success) {
        setUnreadCount(countResponse.data.count);
      }
    } catch (error) {
      console.error('Erro ao carregar notificações:', error);
    } finally {
      setIsLoadingNotifications(false);
    }
  };

  useEffect(() => {
    if (user?.id) {
      loadNotifications();
      // Recarregar notificações a cada 30 segundos
      const interval = setInterval(loadNotifications, 30000);
      return () => clearInterval(interval);
    }
  }, [user?.id]);

  const markAsRead = async (id: string) => {
    const result = await notificacaoService.marcarComoLida(id);
    if (result.success) {
      setNotifications(prev => 
        prev.map(n => n.id === id ? { ...n, lida: true, lida_em: new Date().toISOString() } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    }
  };

  const handleNotificationClick = async (notification: NotificacaoType) => {
    await markAsRead(notification.id);
    navigate(getNotificationRedirectPath(notification));
  };

  const markAllAsRead = async () => {
    const result = await notificacaoService.marcarTodasComoLidas();
    if (result.success) {
      setNotifications(prev => prev.map(n => ({ ...n, lida: true, lida_em: new Date().toISOString() })));
      setUnreadCount(0);
    }
  };

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  return (
    <PrimeirosPassosProvider>
    <SidebarProvider defaultOpen={!isMobile}>
      <div className="flex min-h-screen w-full">
        <AppSidebar />
        <main className="flex-1 overflow-auto">
          <header className="print-hidden sticky top-0 z-10 flex h-14 items-center justify-between gap-2 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 px-2 sm:px-4" style={{ paddingTop: 'env(safe-area-inset-top)', height: 'calc(3.5rem + env(safe-area-inset-top))' }}>
            <div className="flex min-w-0 items-center">
              {/* Trigger só no mobile — no desktop o toggle mora dentro do próprio sidebar (modo ícone) */}
              <SidebarTrigger className="mr-2 shrink-0 md:hidden" />
              <div className="hidden min-w-0 items-center gap-2 text-sm text-muted-foreground sm:flex">
                <span className="shrink-0">Olá,</span>
                <span className="truncate font-medium text-foreground">{user?.nome}</span>
                <span className="ml-2 shrink-0 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                  {user?.tipo === 'superadmin' ? 'Super Admin' : user?.tipo === 'admin' ? 'Admin' : 'Usuário'}
                </span>
              </div>
            </div>

            <div className="flex shrink-0 items-center gap-0">
            {/* Seletores de empresa com busca e em ordem alfabética (2026-10-26). A ordem é só da exibição: a empresa
                padrão do login continua sendo a do primeiro acesso. */}
            {user?.tipo === 'superadmin' && (
              <EmpresaSeletor
                empresas={empresasDisponiveis.map((e) => ({ id: e.id, nome: e.nome }))}
                value={empresaFiltroId ?? null}
                onChange={setEmpresaFiltroId}
                opcaoTodas="Todas as empresas"
              />
            )}
            {user?.tipo !== 'superadmin' && user?.empresas && user.empresas.length > 1 && (
              <EmpresaSeletor
                empresas={user.empresas.map((e) => ({ id: e.id, nome: e.nome, detalhe: e.tipo === 'admin' ? 'Admin' : 'Usuário' }))}
                value={empresaAtiva?.id ?? null}
                onChange={(id) => { if (id) trocarEmpresa(id); }}
              />
            )}
            {user?.tipo === 'superadmin' && (
              <Button
                variant="outline"
                className="h-[34px] gap-1.5 mr-2 text-sm"
                onClick={() => navigate('/empresas')}
              >
                <Building2 className="h-3.5 w-3.5" />
                Empresas
              </Button>
            )}
            <PrimeirosPassosBotao />
            <ThemeToggle />
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="ghost" size="icon" className="relative">
                  <Bell className="h-5 w-5" />
                  {unreadCount > 0 && (
                    <Badge
                      className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs"
                      variant="destructive"
                    >
                      {unreadCount}
                    </Badge>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-80 p-0" align="end">
                <div className="flex items-center justify-between p-4 border-b border-border">
                  <h4 className="font-semibold">Notificações</h4>
                  {unreadCount > 0 && (
                    <Button variant="ghost" size="sm" onClick={markAllAsRead}>
                      Marcar todas como lidas
                    </Button>
                  )}
                </div>
                <ScrollArea className="h-[300px]">
                  {isLoadingNotifications ? (
                    <div className="p-4 text-center text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin mx-auto mb-2" />
                      Carregando...
                    </div>
                  ) : notifications.length === 0 ? (
                    <div className="p-4 text-center text-muted-foreground">
                      Nenhuma notificação
                    </div>
                  ) : (
                    <div className="divide-y divide-border">
                      {notifications.map((notification) => (
                        <div
                          key={notification.id}
                          className={`p-4 cursor-pointer hover:bg-muted/50 transition-colors ${
                            !notification.lida ? 'bg-primary/5' : ''
                          }`}
                          onClick={() => handleNotificationClick(notification)}
                        >
                          <div className="flex gap-3">
                            <div className="flex-shrink-0 mt-0.5">
                              {getNotificationIcon(notification.tipo)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between gap-2">
                                <p className={`text-sm font-medium truncate ${!notification.lida ? 'text-foreground' : 'text-muted-foreground'}`}>
                                  {notification.titulo}
                                </p>
                                {!notification.lida && (
                                  <span className="h-2 w-2 rounded-full bg-primary flex-shrink-0" />
                                )}
                              </div>
                              <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                                {notification.mensagem}
                              </p>
                              <p className="text-xs text-muted-foreground mt-1">
                                {(() => {
                                  const timestamp = getNotificationTimestamp(notification);
                                  return timestamp ? formatTimeAgo(timestamp) : 'Agora';
                                })()}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </PopoverContent>
            </Popover>

              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="ghost" size="icon">
                    <User className="h-5 w-5" />
                  </Button>
                </PopoverTrigger>
              <PopoverContent className="w-56 p-0" align="end">
                <div className="flex items-center gap-3 p-4 border-b border-border">
                  <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                    <User className="h-5 w-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{user?.nome}</p>
                    <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                  </div>
                </div>
                <div className="p-2">
                  <Button
                    variant="ghost"
                    className="w-full justify-start text-sm"
                    onClick={() => navigate('/perfil')}
                  >
                    <User className="h-4 w-4 mr-2" />
                    Meu Perfil
                  </Button>
                  <PrimeirosPassosMenuItem />
                  <Button
                    variant="ghost"
                    className="w-full justify-start text-sm text-red-600 hover:text-red-700 hover:bg-red-50"
                    onClick={() => logout()}
                  >
                    <LogOut className="h-4 w-4 mr-2" />
                    Sair
                  </Button>
                </div>
              </PopoverContent>
              </Popover>
            </div>
          </header>
          <div className="p-3 md:p-6">
            {children}
          </div>
        </main>
      </div>
    </SidebarProvider>
    </PrimeirosPassosProvider>
  );
}
